import React, { useEffect, useRef, useState } from "react";

// The coin turns once and stops.
//
// Continuous spinning reads as "loading", and this isn't loading — the join has
// already succeeded by the time anything here is on screen. One rotation is a
// flourish; a second one starts asking the user to wait. The loop below exists
// only for the case where the league screen is genuinely slow to arrive, so the
// modal isn't sitting frozen while it does.
const SPIN_MS = 900;
// Grace past the caller's own window before we decide the destination is slow.
const SETTLE_GRACE_MS = 250;

const KEYFRAMES = `
@keyframes qushiCoinSpinOnce {
  from { transform: rotateY(0deg); }
  to   { transform: rotateY(360deg); }
}
@keyframes qushiCoinSpinLoop {
  from { transform: rotateY(0deg); }
  to   { transform: rotateY(360deg); }
}
@media (prefers-reduced-motion: reduce) {
  .qushi-coin-spin, .qushi-coin-loop { animation: none !important; }
}
`;

/**
 * The payoff after a league join SUCCEEDS.
 *
 * Only ever rendered once the backend has confirmed the membership — the caller
 * opens it inside the success branch of the join request, never optimistically,
 * so a failed join shows an error and no "You're in" ever appears.
 *
 * Props:
 *   open        — whether the overlay is visible
 *   title       — the league's display name
 *   isPrivate   — which artwork turns: the QUSHi coin for a public league, the
 *                 shield for a private one. The wording is the same either way.
 *   durationMs  — how long the caller waits before it navigates. Keep it between
 *                 1000 and 1300; 2000 is the hard ceiling. The art stays still
 *                 for whatever is left of it after its one rotation.
 */
const LeagueJoinedCelebrationModal = ({
  open,
  title,
  isPrivate = false,
  durationMs = 1200,
}) => {
  // "spin" -> the single rotation, "settled" -> stopped, "loop" -> still waiting.
  const [phase, setPhase] = useState("spin");
  const graceRef = useRef(null);
  const openedAtRef = useRef(0);

  useEffect(() => {
    if (open) {
      openedAtRef.current = Date.now();
      setPhase("spin");
    }
    return () => clearTimeout(graceRef.current);
  }, [open]);

  if (!open) return null;

  const onSpinEnd = () => {
    setPhase("settled");
    // Still here after the window the caller promised? Then the destination is
    // slow, and a motionless coin over a dimmed screen looks stuck rather than
    // finished. Turn again until we're gone.
    const remaining = durationMs - (Date.now() - openedAtRef.current);
    graceRef.current = setTimeout(
      () => setPhase("loop"),
      Math.max(0, remaining) + SETTLE_GRACE_MS
    );
  };

  // Both are cut out from the supplied renders (public_leaque_join.png and
  // joinLeaque.png): the plate each shipped on is gone and the ground shadow
  // survives as real alpha, so neither needs a CSS shadow behind it.
  const art = isPrivate ? "/league-join-shield.png" : "/league-join-coin.png";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-6"
      role="status"
      aria-live="polite"
    >
      <style>{KEYFRAMES}</style>

      <div className="w-full max-w-sm rounded-3xl bg-white px-6 py-8 text-center shadow-2xl">
        {/* Perspective on the wrapper is what makes the Y rotation read as a coin
            turning rather than the image squashing. */}
        <div className="flex justify-center" style={{ perspective: "900px" }}>
          <img
            src={art}
            alt=""
            width="512"
            height="512"
            decoding="async"
            onAnimationEnd={phase === "spin" ? onSpinEnd : undefined}
            className={`h-32 w-32 select-none object-contain md:h-36 md:w-36 ${
              phase === "spin" ? "qushi-coin-spin" : ""
            } ${phase === "loop" ? "qushi-coin-loop" : ""}`}
            style={{
              // Both renders stand upright and face the viewer, so a Y rotation
              // turns them on their own axis rather than skewing them.
              animation:
                phase === "spin"
                  ? `qushiCoinSpinOnce ${SPIN_MS}ms cubic-bezier(0.22, 0.61, 0.36, 1) 1`
                  : phase === "loop"
                  ? "qushiCoinSpinLoop 1400ms linear infinite"
                  : "none",
              transformStyle: "preserve-3d",
            }}
          />
        </div>

        <p className="mt-5 font-montserrat text-3xl font-bold text-[#22A36B]">
          You're in
        </p>
        <p className="mt-1.5 font-montserrat text-lg font-bold leading-snug text-[#0F4146] break-words">
          {title || "Your league"}
        </p>
        <p className="mt-1.5 text-sm text-secondary">Entering league now</p>

        <div className="mx-auto mt-6 h-px w-2/3 bg-divider" />

        <p className="mt-4 text-sm text-secondary">
          League results stay separate from your main track record.
        </p>
      </div>
    </div>
  );
};

export default LeagueJoinedCelebrationModal;
